import { CompositeFilterDescriptor, filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Modal from 'src/components/Modal';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useAttachReassignCompanyApi from 'src/services/useAttachReassignCompanyApi';
import { Companies, Company } from 'src/types/Companies';

function ReassignCompany() {
  const {
    value: isReassignModalOpen,
    setTrue: openReassignModal,
    setFalse: closeReassignModal,
  } = useBoolean();
  const { setRefreshCompany, refreshPage, selectedDocuments, attachmentListData } =
    useInvoiceScanManager();
  const { company } = useCompany();
  const { companies } = useAuth();
  const { confirm } = useConfirm();
  const { attachReassignCompany, isReassignLoading } = useAttachReassignCompanyApi();

  const defaultCompany = useMemo(
    () => companies.find((c) => c.companyId === company.companyId) || ({} as Company),
    [companies, company.companyId]
  );

  const [selectedCompany, setSelectedCompany] = useState<Company>(defaultCompany);

  const isReassignable = useMemo(
    () => selectedCompany?.companyId !== company.companyId,
    [company, selectedCompany]
  );

  const unSelectedCompanies = useMemo(
    () => companies.filter((c) => c.companyId !== company.companyId),
    [companies, company.companyId]
  );

  const [filteredCompanies, setFilteredCompanies] = useState<Companies>(unSelectedCompanies);

  const handleOpenReassignModal = useCallback(() => {
    setSelectedCompany(defaultCompany);
    setFilteredCompanies(unSelectedCompanies);
    openReassignModal();
  }, [defaultCompany, openReassignModal, unSelectedCompanies]);

  const handleCloseReassignModal = useCallback(async () => {
    closeReassignModal();
  }, [closeReassignModal]);

  const handleCompanyChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setSelectedCompany(e.target.value);
    },
    [setSelectedCompany]
  );

  const filterData = (filter: FilterDescriptor | CompositeFilterDescriptor) => {
    const data = [...unSelectedCompanies];
    return filterBy(data, filter);
  };

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    setFilteredCompanies(filterData(event.filter));
  };

  const handleReassign = useCallback(async () => {
    handleCloseReassignModal();
    const isReassignAccepted = await confirm({
      title: 'Reassign to Company',
      content: <div>Are you sure you want to reassign the selected record(s)?</div>,
      confirmButtonText: 'OK',
    });
    if (isReassignAccepted) {
      const selectedIds = selectedDocuments.map((s) => s.attachId);
      await attachReassignCompany({
        attachmentIds: selectedIds,
        sourceCompanyId: defaultCompany.companyId,
        destinationCompanyId: selectedCompany.companyId,
      });
      toast.success('Successfully reassigned to another company!');
      refreshPage();
      setRefreshCompany?.((prev) => !prev);
      setSelectedCompany(defaultCompany);
    }
  }, [
    handleCloseReassignModal,
    confirm,
    selectedDocuments,
    attachReassignCompany,
    defaultCompany,
    selectedCompany.companyId,
    refreshPage,
    setRefreshCompany,
  ]);

  const isIncludeAttached = selectedDocuments.some((d) => d.receiptId);

  const isIncludeDeleted = selectedDocuments.some((d) => d.isDeleted);

  const { isReassignDisabled, informationMessageList, errorMessageList } = useMemo(() => {
    const isReassignDisabled =
      selectedDocuments.length === 0 || isIncludeAttached || isIncludeDeleted;
    const informationMessageList: string[] = [];
    const errorMessageList: string[] = [];
    if (attachmentListData.length === 0) {
      informationMessageList.push('You have no record(s) in queue to Reassign.');
    }
    if (selectedDocuments.length === 0) {
      informationMessageList.push('Please select record(s) to Reassign.');
    }
    if (isIncludeDeleted) {
      errorMessageList.push('Please select only Undeleted record(s) to Reassign.');
    }
    if (isIncludeAttached) {
      errorMessageList.push('Please select only Unattached record(s) to Reassign.');
    }

    return {
      isReassignDisabled,
      informationMessageList,
      errorMessageList,
    };
  }, [attachmentListData.length, isIncludeDeleted, isIncludeAttached, selectedDocuments.length]);

  return (
    <div>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={errorMessageList} />
            <PopUpContentWrapper type="Information" contents={informationMessageList} />
          </div>
        }
        show={isReassignDisabled}
      >
        <Button color="info" disabled={isReassignDisabled} onClick={handleOpenReassignModal}>
          Reassign to Company
        </Button>
      </PopUp>

      <Modal
        title="Reassign to Company"
        visible={isReassignModalOpen}
        closeModal={handleCloseReassignModal}
        width="600px"
        height="220px"
      >
        <div className="mb-2 [&>p]:text-sm [&>p]:mb-2">
          <p>
            Please confirm the company you'd like to reassign the scans to. The selected scans will
            be moved to that company's Scan Manager after reassignment.
          </p>
        </div>

        <div className="flex flex-row items-center">
          <span className="text-sm font-bold mr-2">Select a Company</span>
          <DropDownList
            id="view-drop-down-list"
            className="__selectCompanyDropdown w-md ml-2 bg-white"
            data={filteredCompanies}
            onChange={handleCompanyChange}
            value={selectedCompany}
            textField="companyName"
            dataItemKey="companyId"
            filterable
            onFilterChange={filterChange}
          />
        </div>
        <div className="flex justify-end items-center mt-2">
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
            anchorAlign={{ horizontal: 'center', vertical: 'top' }}
            content={
              <div className="m-1 max-w-[300px]">
                <PopUpContentWrapper
                  type="Error"
                  contents={['Please select another company to Reassign']}
                />
              </div>
            }
            show={!isReassignable}
          >
            <Button
              color="info"
              disabled={!isReassignable}
              onClick={handleReassign}
              loading={isReassignLoading}
            >
              Reassign
            </Button>
          </PopUp>
          <Button color="info" onClick={handleCloseReassignModal} disabled={isReassignLoading}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default ReassignCompany;
